import React, { useEffect, useState } from "react";
import QRCode from "qrcode";
import { QrCode, Download, Copy, Check } from "lucide-react";
import {
  Dialog,
  DialogContent,
  DialogHeader,
  DialogTitle,
  DialogDescription,
  DialogTrigger,
} from "../ui/dialog";
import { Button } from "../ui/button";

// QR code dialog for the owner's Customer Booking Page. The QR encodes the
// EXACT same public link the dashboard card already generates — nothing
// else. Rendered at 1024x1024 with high error correction for print.
export const BookingQrDialog = ({ link, businessName, t }) => {
  const [open, setOpen] = useState(false);
  const [dataUrl, setDataUrl] = useState(null);
  const [copied, setCopied] = useState(false);

  useEffect(() => {
    if (!open || !link) return;
    QRCode.toDataURL(link, {
      errorCorrectionLevel: "H",
      width: 1024,
      margin: 4,
      color: { dark: "#000000", light: "#FFFFFF" },
    })
      .then(setDataUrl)
      .catch(() => setDataUrl(null));
  }, [open, link]);

  const filename =
    `${(businessName || "tasklio").toLowerCase().replace(/[^a-z0-9]+/g, "-").replace(/^-+|-+$/g, "") || "tasklio"}-booking-qr.png`;

  const copyLink = async () => {
    try {
      await navigator.clipboard.writeText(link);
      setCopied(true);
      setTimeout(() => setCopied(false), 2000);
    } catch {
      // clipboard unavailable — the URL is still visible for manual copy.
    }
  };

  return (
    <Dialog open={open} onOpenChange={setOpen}>
      <DialogTrigger asChild>
        <Button
          variant="outline"
          className="h-11 px-5 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50 w-full"
          data-testid="customer-booking-page-qr"
        >
          <QrCode className="w-4 h-4 mr-1.5" /> {t("dash.qrButton")}
        </Button>
      </DialogTrigger>
      <DialogContent className="sm:max-w-md" data-testid="booking-qr-dialog">
        <DialogHeader>
          <DialogTitle className="font-display tracking-tight">{t("dash.qrTitle")}</DialogTitle>
          <DialogDescription>{t("dash.qrHelp")}</DialogDescription>
        </DialogHeader>
        <div className="flex flex-col items-center">
          <div className="p-3 rounded-2xl border-2 border-green-100 bg-white">
            {dataUrl ? (
              <img
                src={dataUrl}
                alt={t("dash.qrTitle")}
                className="w-52 h-52 sm:w-60 sm:h-60"
                data-testid="booking-qr-image"
              />
            ) : (
              <div className="w-52 h-52 sm:w-60 sm:h-60 bg-gray-100 rounded-lg animate-pulse" />
            )}
          </div>
          <p
            className="mt-3 text-xs text-gray-500 break-all text-center px-2"
            data-testid="booking-qr-link"
          >
            {link}
          </p>
          <div className="mt-4 flex flex-col sm:flex-row gap-2 w-full">
            <a
              href={dataUrl || undefined}
              download={filename}
              className={`flex-1 ${dataUrl ? "" : "pointer-events-none opacity-50"}`}
            >
              <Button
                className="h-11 w-full bg-green-800 hover:bg-green-900 text-white font-semibold"
                disabled={!dataUrl}
                data-testid="booking-qr-download"
              >
                <Download className="w-4 h-4 mr-1.5" /> {t("dash.qrDownload")}
              </Button>
            </a>
            <Button
              variant="outline"
              onClick={copyLink}
              className="h-11 flex-1 font-semibold border-gray-300 bg-white text-gray-900 hover:bg-gray-50"
              data-testid="booking-qr-copy"
            >
              {copied ? (
                <><Check className="w-4 h-4 mr-1.5 text-green-700" /> {t("dash.bookingPageCopied")}</>
              ) : (
                <><Copy className="w-4 h-4 mr-1.5" /> {t("dash.bookingPageCopy")}</>
              )}
            </Button>
          </div>
        </div>
      </DialogContent>
    </Dialog>
  );
};
